import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import { Label } from '../Label/Label';
import ClickAwayListener from 'react-click-away-listener';
import styles from './Select.module.scss';

export interface SelectOption<T> {
    label: string;
    value: string;
    data?: T;
}

export interface SelectProps<T> {
    label?: string;
    icon?: IconProp;
    error?: string;
    placeholder?: string;
    className?: string;
    triggerClassName?: string;
    options: Array<SelectOption<T>>;
    value?: string;
    disabled?: boolean;
    required?: boolean;
    renderOption?: (option: SelectOption<T>) => React.ReactNode;
    onChange?: (value: string, option: SelectOption<T>) => void;
}

export const Select = <T,>({
    options,
    className,
    triggerClassName,
    renderOption,
    value,
    onChange,
    placeholder,
    label,
    error,
    icon,
    disabled,
    required,
    ...props
}: SelectProps<T>) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef<HTMLDivElement>(null);

    const selectedOption = options.find(option => option.value === value);

    const handleOptionClick = (option: SelectOption<T>) => {
        onChange?.(option.value, option);
        setIsOpen(false);
    };

    const handleToggle = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    return (
        <div className={clsx(styles.root, className)}>
            {label && (
                <Label
                    error={error}
                    required={required}
                >
                    {label}
                </Label>
            )}

            <ClickAwayListener onClickAway={() => setIsOpen(false)}>
                <div
                    className={styles.selectWrapper}
                    ref={selectRef}
                >
                    <div
                        className={clsx(
                            styles.select,
                            {
                                [styles.selectOpen]: isOpen,
                                [styles.selectFilled]: selectedOption,
                                [styles.selectError]: error,
                                [styles.selectDisabled]: disabled
                            },
                            triggerClassName
                        )}
                        onClick={handleToggle}
                    >
                        <div className={styles.selectContent}>
                            {selectedOption ? (
                                renderOption ? (
                                    renderOption(selectedOption)
                                ) : (
                                    selectedOption.label
                                )
                            ) : (
                                <span className={styles.placeholder}>{placeholder}</span>
                            )}
                        </div>

                        <FontAwesomeIcon
                            icon={icon || faChevronDown}
                            className={clsx(styles.icon, {
                                [styles.iconOpen]: isOpen
                            })}
                        />
                    </div>

                    {isOpen && (
                        <div className={styles.dropdown}>
                            {options.map(option => (
                                <div
                                    key={option.value}
                                    className={clsx(styles.option, {
                                        [styles.optionSelected]: option.value === value
                                    })}
                                    onClick={() => handleOptionClick(option)}
                                >
                                    {renderOption ? renderOption(option) : option.label}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </ClickAwayListener>
        </div>
    );
};

export default Select;
